<template>
  <div class="employee-list-container">

    <h2>员工列表</h2>
    <div class="header-actions" v-if="isAdmin">
      <button class="action-btn primary" @click="showAddDialog = true">➕ 添加员工</button>
    </div>
    
    <!-- 桌面端表格 -->
    <table class="employee-table desktop-table">
      <thead>
        <tr>
          <th>序号</th>
          <th>用户名</th>
          <th>密码</th>
          <th>电话</th>
          <th v-if="isAdmin">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td :colspan="isAdmin ? 5 : 4">正在加载...</td>
        </tr>
        <tr v-if="!loading && employees.length === 0">
          <td :colspan="isAdmin ? 5 : 4">暂无数据</td>
        </tr>
        <tr v-for="(emp, idx) in employees" :key="emp.id || idx">
          <td>{{ (page - 1) * pageSize + idx + 1 }}</td>
          <td>{{ emp.user }}</td>
          <td>{{ emp.password }}</td>
          <td>{{ emp.phone }}</td>
          <td v-if="isAdmin">
            <button class="action-btn" @click="openEditDialog(emp)">编辑</button>
            <button class="action-btn danger" @click="openDeleteDialog(emp)">删除</button>
          </td>
        </tr>
      </tbody>
    </table>
    
    <!-- 移动端卡片列表 -->
    <div class="mobile-employee-list">
      <div v-if="loading" class="loading-card">
        <div class="loading-content">正在加载...</div>
      </div>
      <div v-else-if="!loading && employees.length === 0" class="empty-card">
        <div class="empty-content">暂无数据</div>
      </div>
      <div v-else>
        <div v-for="(emp, idx) in employees" :key="emp.id || idx" class="employee-card">
          <div class="card-header">
            <span class="card-index">#{{ (page - 1) * pageSize + idx + 1 }}</span>
            <span class="card-username">{{ emp.user }}</span>
          </div>
          <div class="card-content">
            <div class="info-row">
              <span class="label">密码：</span>
              <span class="value">{{ emp.password }}</span>
            </div>
            <div class="info-row">
              <span class="label">电话：</span>
              <span class="value">{{ emp.phone || '未填写' }}</span>
            </div>
          </div>
          <div class="card-actions" v-if="isAdmin">
            <el-button size="mini" type="primary" @click="openEditDialog(emp)">编辑</el-button>
            <el-button size="mini" type="danger" @click="openDeleteDialog(emp)">删除</el-button>
          </div>
        </div>
      </div>
    </div>
    <div class="table-pagination-bar">
      <span class="table-count-bottom">
        共{{ total }}条数据 | 第{{ page }} / {{ totalPages }}页
      </span>
      <button class="page-arrow" :disabled="page <= 1" @click="goPrevPage">
        <i class="el-icon-arrow-left"></i>
      </button>
      <button class="page-arrow" :disabled="page >= totalPages" @click="goNextPage">
        <i class="el-icon-arrow-right"></i>
      </button>
    </div>
    <!-- 添加员工弹窗 -->
    <div v-if="showAddDialog" class="dialog-mask">
      <div class="dialog-box">
        <h3>添加员工</h3>
        <form @submit.prevent="handleAddEmployee">
          <div class="form-group">
            <label>用户名 <span class="required">*</span></label>
            <input v-model="addForm.user" type="text" placeholder="请输入用户名" required />
          </div>
          <div class="form-group">
            <label>密码 <span class="required">*</span></label>
            <input v-model="addForm.password" type="password" placeholder="请输入密码" required />
          </div>
          <div class="form-group">
            <label>姓名 <span class="optional">(可选)</span></label>
            <input v-model="addForm.employee" type="text" placeholder="请输入姓名" />
          </div>
          <div class="form-group">
            <label>电话 <span class="optional">(可选)</span></label>
            <input v-model="addForm.phone" type="text" placeholder="请输入电话" />
          </div>
          <div class="form-group">
            <label>选择项目</label>
            <div class="project-type-selector">
              <label class="radio-label">
                <input type="radio" v-model="addForm.projectType" value="A" @change="onAddProjectTypeChange" />
                <span>A类</span>
              </label>
              <label class="radio-label">
                <input type="radio" v-model="addForm.projectType" value="B" @change="onAddProjectTypeChange" />
                <span>B类</span>
              </label>
              <label class="radio-label">
                <input type="radio" v-model="addForm.projectType" value="C" @change="onAddProjectTypeChange" />
                <span>全选</span>
              </label>
              <label class="radio-label">
                <input type="radio" v-model="addForm.projectType" value="D" @change="onAddProjectTypeChange" />
                <span>全不选</span>
              </label>
              <label class="radio-label">
                <input type="radio" v-model="addForm.projectType" value="E" @change="onAddProjectTypeChange" />
                <span>自定义</span>
              </label>
            </div>
            <div class="project-selector-container">
              <div class="project-list">
                <div v-if="projectLoading" class="project-loading">正在加载项目列表...</div>
                <div v-else-if="projectError" class="project-error">{{ projectError }}</div>
                <div v-else class="project-items">
                  <div 
                    v-for="project in projectList" 
                    :key="project.id" 
                    class="project-item"
                  >
                    <el-checkbox 
                      v-model="project.selected" 
                      @change="onProjectSelect"
                    >
                      {{ project.name }}
                    </el-checkbox>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="dialog-actions">
            <button type="submit" class="action-btn primary" :disabled="addLoading">{{ addLoading ? '提交中...' : '提交' }}</button>
            <button type="button" class="action-btn" @click="handleAddCancel">取消</button>
          </div>
          <div v-if="addError" class="add-error">{{ addError }}</div>
        </form>
      </div>
    </div>
    <!-- 编辑员工弹窗 -->
    <div v-if="showEditDialog" class="dialog-mask">
      <div class="dialog-box">
        <h3>编辑员工</h3>
        <form @submit.prevent="handleEditEmployee">
          <div class="form-group">
            <label>用户名</label>
            <input v-model="editForm.user" type="text" required />
          </div>
          <div class="form-group">
            <label>密码</label>
            <input v-model="editForm.password" type="text" required />
          </div>
          <div class="form-group">
            <label>电话 <span class="optional">(可选)</span></label>
            <input v-model="editForm.phone" type="text" />
          </div>
          <div class="form-group">
            <label>选择项目</label>
            <div class="project-type-selector">
              <label class="radio-label">
                <input type="radio" v-model="editForm.projectType" value="A" @change="onEditProjectTypeChange" />
                <span>A类</span>
              </label>
              <label class="radio-label">
                <input type="radio" v-model="editForm.projectType" value="B" @change="onEditProjectTypeChange" />
                <span>B类</span>
              </label>
              <label class="radio-label">
                <input type="radio" v-model="editForm.projectType" value="C" @change="onEditProjectTypeChange" />
                <span>全选</span>
              </label>
              <label class="radio-label">
                <input type="radio" v-model="editForm.projectType" value="D" @change="onEditProjectTypeChange" />
                <span>全不选</span>
              </label>
              <label class="radio-label">
                <input type="radio" v-model="editForm.projectType" value="E" @change="onEditProjectTypeChange" />
                <span>自定义</span>
              </label>
            </div>
            <div class="project-selector-container">
              <div class="project-list">
                <div v-if="editProjectLoading" class="project-loading">正在加载项目列表...</div>
                <div v-else-if="editProjectError" class="project-error">{{ editProjectError }}</div>
                <div v-else class="project-items">
                  <div 
                    v-for="project in editProjectList" 
                    :key="project.projectId" 
                    class="project-item"
                  >
                    <el-checkbox 
                      v-model="project.selected" 
                      @change="onEditProjectSelect"
                    >
                      {{ project.projectName }}
                    </el-checkbox>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="dialog-actions">
            <button type="submit" class="action-btn primary" :disabled="editLoading">{{ editLoading ? '提交中...' : '提交' }}</button>
            <button type="button" class="action-btn" @click="showEditDialog = false">取消</button>
          </div>
          <div v-if="editError" class="add-error">{{ editError }}</div>
        </form>
      </div>
    </div>
    <!-- 删除确认弹窗 -->
    <div v-if="showDeleteDialog" class="dialog-mask">
      <div class="dialog-box delete-confirm-box">
        <h3>确认删除</h3>
        <div class="delete-confirm-content">确定要删除员工 <b>{{ deleteTarget.user }}</b> 吗？</div>
        <div class="dialog-actions">
          <button class="action-btn danger" @click="handleDeleteConfirm" :disabled="deleteLoading">{{ deleteLoading ? '删除中...' : '确认' }}</button>
          <button class="action-btn" @click="showDeleteDialog = false">取消</button>
        </div>
        <div v-if="deleteError" class="add-error">{{ deleteError }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'EmployeeList',
  data() {
    return {
      employees: [],
      loading: false,
      isAdmin: false,
      currentIp: '',
      showAddDialog: false,
      addForm: {
        user: '',
        password: '',
        employee: '',
        phone: '',
        selectedProjects: [],
        projectType: 'C'
      },
      addLoading: false,
      addError: '',
      page: 1,
      total: 0,
      totalPages: 1,
      pageSize: 10, // 默认每页10条，后端如有变化请同步
      showEditDialog: false,
      editForm: {
        id: '',
        user: '',
        password: '',
        phone: '',
        selectedProjects: [],
        projectType: 'E'
      },
      editLoading: false,
      editError: '',
      showDeleteDialog: false,
      deleteTarget: {},
      deleteLoading: false,
      deleteError: '',
      projectList: [],
      projectLoading: false,
      projectError: null,
      editProjectList: [],
      editProjectLoading: false,
      editProjectError: null,
    }
  },
  created() {
    this.checkAdmin();
    this.fetchEmployees();
    this.fetchIp();
    this.loadProjects(); // 在组件创建时加载项目列表
  },
  methods: {
    async fetchEmployees(page = this.page) {
      this.loading = true;
      try {
        // const res = await axios.get(`/api/employees?authority=2&page=${page}`);
        const res = await axios.get(`/api/employees/getAllEmployee?page=${page}`);
        this.employees = res.data.data || [];
        this.total = res.data.total || 0;
        this.totalPages = res.data.totalPages || 1;
        this.page = page;
        // 自动推断每页条数
        if (this.employees.length > 0 && this.totalPages > 0) {
          this.pageSize = Math.ceil(this.total / this.totalPages);
        }
      } finally {
        this.loading = false;
      }
    },
    goPrevPage() {
      if (this.page <= 1) return;
      this.fetchEmployees(this.page - 1);
    },
    goNextPage() {
      if (this.page >= this.totalPages) return;
      this.fetchEmployees(this.page + 1);
    },
    async fetchIp() {
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        const data = await res.json();
        this.currentIp = data.ip;
      } catch (e) {
        this.currentIp = '获取失败';
      }
    },
    authorityText(auth) {
      return auth == 1 ? '管理员' : '普通员工';
    },
    formatTime(time) {
      if (!time) return '';
      const d = new Date(time);
      const y = d.getFullYear();
      const m = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      const h = d.getHours().toString().padStart(2, '0');
      const min = d.getMinutes().toString().padStart(2, '0');
      return `${y}-${m}-${day} ${h}:${min}`;
    },
    async checkAdmin() {
      try {
        // 获取用户信息
        const userInfoRes = await axios.get('/api/user/info');
        const userInfo = userInfoRes.data;
        this.isAdmin = userInfo.authority == '1';
      } catch (error) {
        console.error('获取用户权限失败:', error);
        this.isAdmin = false;
      }
    },
    async handleAddEmployee() {
      this.addError = '';
      if (!this.addForm.user || !this.addForm.password) {
        this.addError = '请填写所有信息';
        return;
      }
      // 获取选中的项目
      const selectedProjectIds = this.projectList.filter(project => project.selected).map(project => project.id);
      this.addLoading = true;
      try {
        // 构造完整payload
        const now = new Date();
        const pad = n => n < 10 ? '0' + n : n;
        const nowStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
        // 判断是否全选
        const isAllSelected = this.projectList.length > 0 && this.projectList.every(project => project.selected);
        const payload = {
          user: this.addForm.user,
          password: this.addForm.password,
          employee: this.addForm.employee,
          phone: this.addForm.phone,
          email: '',
          address: '',
          department: '',
          city: '',
          time: nowStr,
          authority: isAllSelected == true ? 2 : 3,
          projectIds: selectedProjectIds
        };
        const response = await axios.post('/api/employees/add', payload);
        if(response.data.success == false) {
          this.addError = response.data.message;
        }
        else {
          this.showAddDialog = false;
          this.addForm = { user: '', password: '', employee: '', phone: '', projectType: 'C' };
          this.fetchEmployees();
        }
      } catch (e) {
        this.addError = '添加失败，请稍后重试';
      } finally {
        this.addLoading = false;
      }
    },
    handleAddCancel() {
      this.showAddDialog = false;
      this.addForm = { user: '', password: '', employee: '', phone: '', projectType: 'C' };
    },
    async openEditDialog(emp) {
        try {
          const response = await axios.get(`/api/employeeAndProject/getByEmployeeId?employeeId=${emp.id}`);
          if (response.data.success) {
            // 提取projectId数组
            const projectIds = response.data.data.map(item => item.projectId);
            this.editForm = {
              id: emp.id,
              user: emp.user,
              password: emp.password,
              phone: emp.phone,
              selectedProjects: projectIds,
              projectType: 'E'
            };
            this.loadEditProjects(emp.id);
            this.editError = '';
            this.showEditDialog = true;
          } else {
            console.log(response);
          }
        } catch (error) {
          console.log(error);
        } finally {
          console.log("");
        }
    },
    async loadEditProjects(employeeId) {
      this.editProjectLoading = true;
      this.editProjectError = null;
      try {
        //const currentEmployeeId = localStorage.getItem('employeeId');
        const response = await axios.get(`/api/employeeAndProject/getAllProjectsWithStatus?employeeId=${employeeId}`);
        if (response.data.success) {
          this.editProjectList = response.data.data.map(project => ({
            ...project,
            selected: project.isAssociated == 1 ? true : false
          }));
        } else {
          this.editProjectError = '获取项目列表失败: ' + (response.data.message || '未知错误');
        }
      } catch (error) {
        this.editProjectError = '获取项目列表失败: ' + error;
      } finally {
        this.editProjectLoading = false;
      }
    },
    async handleEditEmployee() {
      this.editError = '';
      if (!this.editForm.user || !this.editForm.password) {
        this.editError = '请填写所有信息';
        return;
      }
      // 获取选中的项目
      const selectedProjectIds = this.editProjectList.filter(project => project.selected).map(project => project.projectId);
      this.editLoading = true;
      try {
        // 需要补全后端需要的字段
        //const emp = this.employees.find(e => e.id === this.editForm.id);
        const isAllSelected = this.editProjectList.length > 0 && this.editProjectList.every(project => project.selected);
        const payload = {
          id: this.editForm.id,
          user: this.editForm.user,
          password: this.editForm.password,
          phone: this.editForm.phone,
          authority: isAllSelected == true || this.editForm.user == "员工1" ? 2 : 3,
          projectIds: selectedProjectIds
        };
        console.log(payload);
        console.log(payload.authority);
        console.log(typeof payload.authority);
        const response = await axios.post('/api/employees/update', payload);
        if(response.data.success == false) {
          this.editError = response.data.message;
        }
        else {
          this.showEditDialog = false;
          this.fetchEmployees();
        }
      } catch (e) {
        this.editError = '编辑失败，请稍后重试';
      } finally {
        this.editLoading = false;
      }
    },
    openDeleteDialog(emp) {
      this.deleteTarget = emp;
      this.deleteError = '';
      this.showDeleteDialog = true;
    },
    async handleDeleteConfirm() {
      this.deleteError = '';
      this.deleteLoading = true;
      try {
        await axios.post('/api/employees/delete', { id: this.deleteTarget.id });
        this.showDeleteDialog = false;
        this.fetchEmployees();
      } catch (e) {
        this.deleteError = '删除失败，请稍后重试';
      } finally {
        this.deleteLoading = false;
      }
    },
    addEmployee() {
      this.showAddDialog = true;
    },
    deleteEmployee() {
      // 废弃，已用弹窗确认
    },
    async loadProjects() {
      this.projectLoading = true;
      this.projectError = null;
      try {
        // 获取用户信息
        const userInfoRes = await axios.get('/api/user/info');
        const userInfo = userInfoRes.data;
        const authority = userInfo.authority;
        const employeeId = userInfo.employeeId;
        
        let url;
        if (authority == 1) {
          url = `/api/projects`;
        } else {
          url = `/api/projects/byEmployee?employeeId=${employeeId}`;
        }
        const response = await axios.get(url);
        if (response.data.success) {
          this.projectList = response.data.data.map(project => ({
            ...project,
            selected: true
          }));
        } else {
          this.projectError = '获取项目列表失败: ' + (response.data.message || '未知错误');
        }
      } catch (error) {
        this.projectError = '获取项目列表失败: ' + error;
      } finally {
        this.projectLoading = false;
      }
    },
    onProjectSelect() {
      // 当手动修改复选框时，设置为自定义模式
      this.addForm.projectType = 'E';
    },
    onEditProjectSelect() {
      // 当手动修改复选框时，设置为自定义模式
      this.editForm.projectType = 'E';
    },
    onAddProjectTypeChange() {
      if (this.addForm.projectType === 'A') {
        // A类：选中type为1的项目，不选中type为2的项目
        this.projectList.forEach(project => {
          project.selected = project.type === 1;
        });
      } else if (this.addForm.projectType === 'B') {
        // B类：选中type为2的项目，不选中type为1的项目
        this.projectList.forEach(project => {
          project.selected = project.type === 2;
        });
      } else if (this.addForm.projectType === 'C') {
        // 自定义：全选所有项目
        this.projectList.forEach(project => {
          project.selected = true;
        });
      } else if (this.addForm.projectType === 'D') {
        // 自定义：全不选所有项目
        this.projectList.forEach(project => {
          project.selected = false;
        });
      } else if (this.addForm.projectType === 'E') {
        // 自定义：全选所有项目
        //this.projectList.forEach(project => {
        //  project.selected = true;
        //});
      }
    },
    onEditProjectTypeChange() {
      if (this.editForm.projectType === 'A') {
        // A类：选中type为1的项目，不选中type为2的项目
        this.editProjectList.forEach(project => {
          project.selected = Number(project.projectType) === 1;
        });
      } else if (this.editForm.projectType === 'B') {
        // B类：选中type为2的项目，不选中type为1的项目
        this.editProjectList.forEach(project => {
          project.selected = Number(project.projectType) === 2;
        });
      } else if (this.editForm.projectType === 'C') {
        // 自定义：全选所有项目
        this.editProjectList.forEach(project => {
          project.selected = true;
        });
      } else if (this.editForm.projectType === 'D') {
        // 自定义：全不选所有项目
        this.editProjectList.forEach(project => {
          project.selected = false;
        });
      } else if (this.editForm.projectType === 'E') {
        // 自定义：全选所有项目
        //this.editProjectList.forEach(project => {
        //  project.selected = true;
        //});
      }
    }
  }
}
</script>

<style scoped>
.employee-list-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 24px;
}
.header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}
.action-btn {
  background-color: #fff;
  color: #444;
  border: 1px solid #dcdfe6;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}
.action-btn.primary {
  background-color: #1890ff;
  color: white;
  border-color: #1890ff;
}
.action-btn.primary:hover {
  background-color: #40a9ff;
  border-color: #40a9ff;
  color: white;
}
.action-btn.danger {
  background-color: #ff4d4f;
  color: #fff;
  border-color: #ff4d4f;
}
.action-btn.danger:hover {
  background-color: #ff7875;
  border-color: #ff7875;
}
.employee-list-container h2 {
  margin: 0 0 20px 0;
  font-size: 20px;
  color: #333;
}
.employee-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}
.employee-table th, .employee-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  text-align: center;
}
.employee-table th {
  background: #f5f5f5;
  font-weight: 600;
  color: #333;
}
.employee-table tr:hover {
  background: #f8fafc;
}
.dialog-mask {
  position: fixed;
  left: 0; top: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.18);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dialog-box {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.13);
  padding: 32px 28px 24px 28px;
  min-width: 320px;
  max-width: 90vw;
}
.dialog-box h3 {
  margin: 0 0 18px 0;
  font-size: 20px;
  color: #333;
  text-align: center;
}
.delete-confirm-box {
  min-width: 300px;
  max-width: 90vw;
  text-align: center;
}
.delete-confirm-content {
  font-size: 16px;
  margin: 18px 0 10px 0;
  color: #333;
}
.form-group {
  margin-bottom: 18px;
}
.form-group label {
  display: block;
  margin-bottom: 6px;
  color: #333;
  font-size: 15px;
}
.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 15px;
  box-sizing: border-box;
  transition: border-color 0.2s;
}
.form-group input:focus {
  border-color: #1890ff;
  outline: none;
}
.dialog-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 10px;
}
.add-error {
  color: #ff4d4f;
  text-align: center;
  margin-top: 10px;
  font-size: 14px;
}
.table-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
  gap: 8px;
  width: 100%;
}
.table-count-bottom {
  color: #888;
  font-size: 15px;
  min-width: 180px;
  text-align: right;
}
.page-arrow {
  border: none;
  background: none;
  font-size: 18px;
  color: #42b983;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}
.page-arrow:disabled {
  color: #ccc;
  cursor: not-allowed;
  background: none;
}
.page-arrow:not(:disabled):hover {
  background: #e6f7f1;
}
.project-selector-container {
  margin-top: 10px;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background-color: #fafafa;
}
.project-list {
  padding: 8px 0;
}
.project-items {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.project-item {
  margin-right: 16px;
  margin-bottom: 8px;
}
.project-loading, .project-error {
  text-align: center;
  padding: 20px 0;
  color: #888;
  font-size: 14px;
}
.project-error {
  color: #ff4d4f;
}
.required {
  color: #ff4d4f;
  font-size: 14px;
}
.optional {
  color: #999;
  font-size: 12px;
}
.project-type-selector {
  margin-bottom: 12px;
  display: flex;
  gap: 20px;
}
.radio-label {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
}
.radio-label input[type="radio"] {
  margin: 0;
  cursor: pointer;
  width: 14px;
  height: 14px;
}
.radio-label span {
  user-select: none;
  margin-left: 2px;
}

/* 移动端响应式样式 */
@media (max-width: 768px) {
  .employee-list-container {
    padding: 16px;
  }
  
  .employee-list-container h2 {
    font-size: 18px;
    text-align: center;
  }
  
  .header-actions {
    justify-content: center;
  }
  
  .header-actions .action-btn {
    width: 100%;
    justify-content: center;
  }
  
  /* 桌面端表格隐藏 */
  .desktop-table {
    display: none;
  }
  
  /* 移动端卡片显示 */
  .mobile-employee-list {
    display: block;
  }
  
  .loading-card,
  .empty-card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    margin-bottom: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .loading-content,
  .empty-content {
    color: #666;
    font-size: 14px;
  }
  
  .employee-card {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    margin-bottom: 12px;
    padding: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }
  
  .card-index {
    background: #1890ff;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
  }
  
  .card-username {
    font-weight: bold;
    color: #333;
    font-size: 16px;
  }
  
  .card-content {
    margin-bottom: 12px;
  }
  
  .info-row {
    display: flex;
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 1.4;
  }
  
  .info-row .label {
    color: #666;
    min-width: 60px;
    flex-shrink: 0;
  }
  
  .info-row .value {
    color: #333;
    flex: 1;
    word-break: break-all;
  }
  
  .card-actions {
    text-align: center;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    gap: 8px;
    justify-content: center;
  }
  
  /* 分页器移动端优化 */
  .table-pagination-bar {
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
    overflow-x: auto;
    padding: 8px 0;
  }
  
  .table-count-bottom {
    text-align: center;
    min-width: auto;
  }
  
  /* 弹窗移动端优化 */
  .dialog-box {
    margin: 16px;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .project-type-selector {
    flex-direction: column;
    gap: 12px;
  }
  
  .project-items {
    flex-direction: column;
    gap: 8px;
  }
  
  .project-item {
    margin-right: 0;
    margin-bottom: 4px;
  }
}

/* 桌面端隐藏移动端卡片 */
@media (min-width: 769px) {
  .mobile-employee-list {
    display: none;
  }
  
  .desktop-table {
    display: table;
  }
}
</style>